import { Box, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import sidebarConfig from "./SidebarConfig";
import { useContentSetting } from "../../Hooks/ContentSetting";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/PersonOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { logo } from "../../assets";

// Sizing is lifted from the consumer app's Learning Hub rail
// (web-frontend `pages/learning-hub/[[...params]].jsx` — `styles.sidebar` and
// friends) so the admin portal reads as the same product rather than a generic
// MUI admin panel. Same widths, same breakpoints, same row metrics, same
// icon-tile treatment. If those change over there, change them here too.
const EXPANDED_WIDTH = 260;   // 230 in the 600–640px band, matching the hub
const COLLAPSED_WIDTH = 44;   // 40 in that same band — a genuine icon-only rail
const NARROW_EXPANDED_WIDTH = 230;
const NARROW_COLLAPSED_WIDTH = 40;

// The hub's rail is a red gradient, not a white panel — which is also the only
// background the logo asset works on, since it is solid white with an alpha
// channel and was invisible on the previous #ffffff sidebar.
const sidebarBg = "linear-gradient(180deg, #a8192a 0%, #c01e2e 40%, #8b1522 100%)";
const onRedBorder = "rgba(255,255,255,0.10)";
const onRedText = "rgba(255,255,255,0.55)";
const onRedLabel = "rgba(255,255,255,0.35)";
const onRedTile = "rgba(255,255,255,0.06)";
const font = "'Rubik', system-ui, sans-serif";

export default function AppSidebar({ handleDrawerToggle, mobileOpen }) {
  const { userInfo, setUserInfo } = useContentSetting();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(true);
  const sidebarRef = useRef(null);

  // The permanent rail only exists from `sm` (600px) up, so this is the hub's
  // 640px step applied to the narrow end of that range.
  const narrow = useMediaQuery("(max-width:640px)");
  const expandedWidth = narrow ? NARROW_EXPANDED_WIDTH : EXPANDED_WIDTH;
  const collapsedWidth = narrow ? NARROW_COLLAPSED_WIDTH : COLLAPSED_WIDTH;

  const menuItems = sidebarConfig();

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && !collapsed) {
        setCollapsed(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [collapsed]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_data");
    setUserInfo(null);
    navigate("/login");
  };

  const getUserName = () => {
    if (userInfo?.first_name && userInfo?.last_name)
      return `${userInfo.first_name} ${userInfo.last_name}`;
    if (userInfo?.firstName && userInfo?.lastName)
      return `${userInfo.firstName} ${userInfo.lastName}`;
    return userInfo?.name || userInfo?.email || "Admin";
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const currentWidth = collapsed ? collapsedWidth : expandedWidth;

  // One row = a fixed icon tile plus a 13px label, exactly as the hub draws it.
  // The per-item `description` from SidebarConfig moves into the tooltip: the
  // hub's rows are a single line, and stacking a second line of 11px copy under
  // every title is what made this rail feel like a different application.
  const renderNavItem = (item, expanded, isMobile) => {
    const active = isActive(item.path);
    return (
      <Tooltip
        key={item.id}
        title={expanded ? item.description || "" : item.title}
        placement="right"
        enterDelay={400}
      >
        <div
          onClick={() => {
            navigate(item.path);
            if (isMobile) handleDrawerToggle();
          }}
          style={{
            padding: expanded ? "10px 14px" : "6px 0",
            margin: expanded ? "0 8px" : "0 4px",
            borderRadius: 12,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: expanded ? "flex-start" : "center",
            gap: "10px",
            background: active ? "rgba(0,0,0,0.16)" : "transparent",
            transition: "all 0.15s ease",
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => {
            if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.08)";
          }}
          onMouseLeave={(e) => {
            if (!active) e.currentTarget.style.background = "transparent";
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: expanded ? 34 : 26,
              height: expanded ? 34 : 26,
              borderRadius: expanded ? "10px" : "8px",
              background: active ? "#fff" : onRedTile,
              color: active ? "#c01e2e" : "rgba(255,255,255,0.5)",
              transition: "all 0.15s ease",
              "& svg": { fontSize: expanded ? "18px" : "14px", color: "inherit" },
            }}
          >
            {item.icon}
          </Box>

          {expanded && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: active ? 700 : 500,
                  color: active ? "#fff" : onRedText,
                  fontFamily: font,
                  letterSpacing: "0.01em",
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.title}
              </Typography>
            </div>
          )}
        </div>
      </Tooltip>
    );
  };

  // Rendered once for the desktop permanent rail (respects `collapsed`) and once
  // for the mobile temporary overlay (always full-width/expanded). Both Drawer
  // variants used to share one pre-built element, so the mobile drawer rendered
  // the desktop's icon-only layout inside a full-width paper, and the
  // click-outside ref was claimed by whichever copy mounted last.
  const renderDrawer = (isMobile) => {
    const expanded = isMobile ? true : !collapsed;
    const width = isMobile ? EXPANDED_WIDTH : currentWidth;

    return (
      <div
        ref={isMobile ? null : sidebarRef}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: sidebarBg,
          width,
          transition: isMobile ? undefined : "width 0.25s cubic-bezier(0.4,0,0.2,1)",
          overflow: "hidden",
        }}
      >
        {/* Header — logo + collapse toggle */}
        <div
          style={{
            // Hub metrics are 12px/10px; the collapsed rail keeps the vertical
            // 12px but drops to 4px horizontally, because the wordmark is
            // 1.95:1 and 10px of padding clips it inside a 44px rail.
            padding: expanded ? "12px 10px" : "12px 4px",
            borderBottom: `1px solid ${onRedBorder}`,
            display: "flex",
            justifyContent: expanded ? "space-between" : "center",
            alignItems: "center",
            minHeight: "44px",
            gap: "10px",
          }}
        >
          <img
            src={logo}
            alt="XAir"
            style={{
              height: expanded ? 36 : 16,
              width: "auto",
              objectFit: "contain",
              flexShrink: 0,
              filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.2))",
            }}
          />
          {expanded && !isMobile && (
            <IconButton
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
              sx={{
                background: "rgba(255,255,255,0.10)",
                border: "none",
                borderRadius: "8px",
                padding: "7px",
                color: "rgba(255,255,255,0.7)",
                flexShrink: 0,
                transition: "all 0.15s ease",
                "&:hover": { background: "rgba(255,255,255,0.20)", color: "#fff" },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </div>

        {/* Section label */}
        {expanded && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: onRedLabel,
              letterSpacing: "0.1em",
              padding: "16px 18px 6px",
              fontFamily: font,
            }}
          >
            ADMIN
          </div>
        )}

        {/* Navigation */}
        <div
          style={{
            flex: 1,
            padding: "4px 0",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {menuItems.map((item) => renderNavItem(item, expanded, isMobile))}
        </div>

        {/* Expand button, when collapsed */}
        {!expanded && (
          <div style={{ padding: "4px 0", display: "flex", justifyContent: "center" }}>
            <IconButton
              onClick={() => setCollapsed(false)}
              aria-label="Expand sidebar"
              sx={{
                background: "rgba(255,255,255,0.08)",
                border: "none",
                borderRadius: "6px",
                padding: "5px",
                color: "rgba(255,255,255,0.5)",
                transition: "all 0.15s ease",
                "&:hover": { background: "rgba(255,255,255,0.18)", color: "#fff" },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </div>
        )}

        {/* User section */}
        <div
          style={{
            padding: expanded ? "14px" : "14px 0",
            borderTop: `1px solid ${onRedBorder}`,
            marginTop: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: expanded ? "stretch" : "center",
            gap: "8px",
          }}
        >
          {expanded ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PersonIcon sx={{ fontSize: 16, color: "#c01e2e" }} />
              </div>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#fff",
                  fontFamily: font,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {getUserName()}
              </Typography>
            </div>
          ) : (
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonIcon sx={{ fontSize: 13, color: "#c01e2e" }} />
            </div>
          )}

          <IconButton
            onClick={handleLogout}
            disableRipple
            title="Sign out"
            aria-label="Sign out"
            sx={{
              background: "rgba(255,255,255,0.10)",
              border: "none",
              borderRadius: "8px",
              padding: "7px",
              color: "rgba(255,255,255,0.7)",
              width: expanded ? "100%" : "28px",
              height: "30px",
              transition: "all 0.15s ease",
              "&:hover": { background: "rgba(255,255,255,0.20)", color: "#fff" },
            }}
          >
            <LogoutIcon sx={{ fontSize: expanded ? 16 : 13 }} />
          </IconButton>
        </div>
      </div>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: currentWidth },
        flexShrink: { sm: 0 },
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
      }}
      aria-label="admin navigation"
      className="app-sidebar-box"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: EXPANDED_WIDTH,
            background: sidebarBg,
            border: "none",
          },
        }}
      >
        {renderDrawer(true)}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: currentWidth,
            background: sidebarBg,
            border: "none",
            boxShadow: "4px 0 24px rgba(0,0,0,0.25)",
            transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
            overflowX: "hidden",
          },
        }}
        open
      >
        {renderDrawer(false)}
      </Drawer>
    </Box>
  );
}
